/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import { withRouter } from 'react-router-dom'
import qs from 'query-string'
import moment from 'moment'

import {
  Button,
  CenteredContainerDisplay,
  Heading,
  Modal,
  Spinner,
  Warning,

  FilterSidebar,
  PicklistWithData,
  EmployeePicklist,
  WorkflowPicklist,
  Tasks,
  SvgIcon
} from '../components'


const getInitialState = (queryString) => {
  let defaultState = {
    statusFilter: 'ready',
    workflowRunStatusFilter: 'processing',
    workflowFilter: '',
    assignedToFilter: '',
    startDate: null,
    endDate: null,
    search: ''
  }

  if (queryString.length > 0) {
    return { ...defaultState, ...getFiltersFromQueryString(queryString) }
  } else {
    return defaultState
  }
}

const getFiltersFromQueryString = (queryString) => {
  let queryParams = qs.parse(queryString)

  let filters = Object.assign(
    {},
    ...Object.keys(queryParams).map(key => (  // ES6 way of assigning renamed object keys
      key === 'search'
        ? { [key]: queryParams[key] }
        : [ "startDate", "endDate" ].indexOf(key) > -1  // end/startDate query params aren't translated into state variables with -'Filter' appended like the others
          ? { [ key ]: moment(queryParams[key], "YYYYMMDD") }
          : { [`${key}Filter`]: queryParams[key] }
    ))
  )

  return filters
}


class TaskList extends React.PureComponent {
  state = getInitialState(this.props.location.search)

  componentDidMount() {
    if (this.props.location.search.length === 0) {
      this.setQueryString(true)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.location.search !== this.props.location.search) {
      this.setState(getInitialState(this.props.location.search))
    }
  }

  setQueryString = (firstRender) => {
    let { search, statusFilter, workflowRunStatusFilter, workflowFilter, assignedToFilter, startDate, endDate } = this.state

    let filters = {
      status: statusFilter
    }

    if (search !== '') {
      filters.search = search
    }

    if (workflowRunStatusFilter !== '') {
      filters.workflowRunStatus = workflowRunStatusFilter
    }

    if (workflowFilter !== '') {
      filters.workflow = workflowFilter
    }

    if (firstRender) {
      filters.assignedTo = localStorage.getItem("userUUID")
    } if (assignedToFilter !== '') {
      filters.assignedTo = assignedToFilter
    }

    if (startDate) {
      filters.startDate = startDate.format("YYYYMMDD")
    }
    if (endDate) {
      filters.endDate = endDate.format("YYYYMMDD")
    }

    if (firstRender) {
      this.props.history.replace({ search: qs.stringify(filters) })
    } else {
      this.props.history.push({ search: qs.stringify(filters) })
    }
  }

  showProcessingUpload = () => (
    <Modal
      body={
        <CenteredContainerDisplay
          height={300}
          yAdjustPercentage={-5}
          content={
            <Spinner type="track">
              Processing
            </Spinner>
          }
        />
      }
    />
  )

  showUploadConfirmModal = () => (
    <Modal
      body={
        <div>
          <Heading className="slds-align_absolute-center">
            {this.state.uploadMessage}
          </Heading>
        </div>
      }
      footer={
        <Button
          onClick={() => this.setState({ uploadConfirm: false })}
          className="slds-align_absolute-center">
          OK
        </Button>
      }
    />
  )

  showUploadError = () => (
    <Modal
      body={
        <div>
          <Warning className="slds-align_absolute-center slds-m-bottom_medium" iconSize="medium">
            <Heading className="slds-align_absolute-center">
              File upload failed.
            </Heading>
          </Warning>
        </div>
      }
      footer={
        <Button
          onClick={() => this.setState({ uploadError: false })}
          className="slds-align_absolute-center">
          OK
        </Button>
      }
    />
  )

  uploadNewClientsFile = (e) => {
    let file = e.target.files[0]
    let body = new FormData()
    body.append('file', file)

    this.setState({ processingUpload: true })
    fetch(`${process.env.REACT_APP_API_ROOT}/v1/newclientsupload/`, {
      method: 'post',
      headers: {
        "Authorization": "Token " + localStorage.getItem('token') || null
      },
      body: body
    })
    .then((response) => {
      if (response.ok) {  // covers 200-range of http responses
        console.log("Upload request successful.")
        response.json()
        .then((data) => {
          this.setState({
            uploadMessage: data.message,
            uploadConfirm: true,
            processingUpload: false
          })
        }, () => console.log("Something went wrong..."))
      } else {
        console.log(`Upload request failed.  HTTP: ${response.status} - ${response.statusText}`)
        this.setState({
          uploadError: true,
          processingUpload: false
        })
      }
    })
    .catch((error) => {
      console.log('Upload request failed', error)
      this.setState({
        uploadError: true,
        processingUpload: false
      })
    })
  }

  render() {
    let filters = [
      {
        title: 'Status',
        options: <PicklistWithData
          id='status'
          forField='WorkflowRunStepStatus'
          default='Ready'
          symbol='chevrondown'
          value={this.state.statusFilter}
          onChange={status => this.setState({ statusFilter: status }, this.setQueryString)}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Workflow run status',
        options: <PicklistWithData
          id='workflowRunStatus'
          forField='WorkflowRunStatus'
          default='Processing'
          symbol='chevrondown'
          value={this.state.workflowRunStatusFilter}
          onChange={status => this.setState({ workflowRunStatusFilter: status }, this.setQueryString)}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Workflow',
        options: <WorkflowPicklist
          id='workflow'
          default='All'
          value={this.state.workflowFilter}
          onChange={workflow => (
            this.setState(
              { workflowFilter: workflow === "All" ? '' : workflow }, this.setQueryString
            )
          )}
        />
      },
      {
        title: 'Assigned to',
        options: <EmployeePicklist
          id='assignedTo'
          default='Anyone'
          symbol='chevrondown'
          value={this.state.assignedToFilter}
          onChange={assignedTo => this.setState({ assignedToFilter: assignedTo }, this.setQueryString)}
          closeDropdownOnSelect={true}
          inline={true}
        />
      }
    ]

    return (
      <div className="slds-grow slds-scrollable_y" role="main">
        {this.state.processingUpload && this.showProcessingUpload()}
        {this.state.uploadConfirm && this.showUploadConfirmModal()}
        {this.state.uploadError && this.showUploadError()}

        <div className="slds-grid slds-wrap slds-size_1-of-1 slds-clearfix">
          <div className="tab-nav slds-size_1-of-1 u-bg-color-white">
            <div className="u-font-size-x-large u-text-caps slds-p-left_large slds-m-bottom_small slds-grid">
              <SvgIcon
                variant="utility"
                symbol="task"
                colour="light"
                className="slds-m-right_medium slds-m-top_x-small"
              />
              <div className="slds-m-top_small">Tasks</div>
            </div>
          </div>
          <FilterSidebar
            actions={
              <div>
                <Button
                  onClick={() =>
                    document.getElementById("uploadNewClientsInput").click()
                  }
                  className="slds-size_1-of-1"
                  minWidth={100}
                >
                  Import New Clients
                </Button>
                {/* programmatically trigger file browser using input */}
                <input
                  id="uploadNewClientsInput"
                  type="file"
                  style={{ display: "none" }}
                  onClick={(e) => (e.target.value = null)} // this line was necessary to allow the same file to be uploaded back-to-back
                  onChange={this.uploadNewClientsFile}
                />
              </div>
            }
            filters={filters}
            filtersApplied={
              [
                this.state.statusFilter === "ready"
                  ? ""
                  : this.state.statusFilter,
                this.state.workflowRunStatusFilter === "processing"
                  ? ""
                  : this.state.workflowRunStatusFilter,
                this.state.workflowFilter,
                this.state.assignedToFilter,
              ].filter((filter) => filter.length > 0).length
            }
            onClear={() =>
              this.setState(
                {
                  statusFilter: "ready",
                  workflowRunStatusFilter: "processing",
                  workflowFilter: "",
                  assignedToFilter: "",
                  startDate: null,
                  endDate: null,
                },
                this.setQueryString
              )
            }
            search={true}
            searchString={this.state.search}
            onSearchClick={(search) => this.setState({ search })}
            onSearchClear={() => this.setState({ search: "" })}
            dateRange={true}
            dateRangeLabel="Scheduled date range:"
            startDate={this.state.startDate}
            endDate={this.state.endDate}
            onStartDateChange={(date) =>
              this.setState({ startDate: date }, this.setQueryString)
            }
            onEndDateChange={(date) =>
              this.setState({ endDate: date }, this.setQueryString)
            }
          />
          <div className="slds-col slds-size_10-of-12">
            <Tasks
              search={this.state.search || ""}
              status={this.state.statusFilter}
              workflowRunStatus={this.state.workflowRunStatusFilter}
              workflow={this.state.workflowFilter}
              assignedTo={this.state.assignedToFilter}
              startDate={
                this.state.startDate
                  ? moment
                      .utc(this.state.startDate)
                      .format("YYYY-MM-DD HH:mm:ss.SSS")
                  : null
              }
              endDate={
                this.state.endDate
                  ? moment
                      .utc(this.state.endDate)
                      .format("YYYY-MM-DD HH:mm:ss.SSS")
                  : null
              }
            />
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(TaskList)



// WEBPACK FOOTER //
// ./src/containers/TaskList.js